import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '../../app/appStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { ticketLabel, ticketSerial } from '../../shared/raffle/ticketSerial';
import { useAuthStore } from '../auth/authStore';
import { fmtInt, fmtMoney } from '../../shared/lib/format';
import { localListTimeSec, shortDateTime } from '../../shared/lib/datetime';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { settledPoolOf } from '../../shared/game/rafflePrize';
import { defaultAvatarUrl } from '../../shared/profile/avatarDefaults';
import { GuestAuthSheet } from '../auth/GuestAuthSheet';
import { GuestJoinFooter } from '../auth/GuestJoinFooter';
import { PublicProfileSheet, profileSeed } from '../profile/PublicProfileSheet';
import { fetchRaffleResult } from '../wallet/api';
import type { RaffleRecentEvent } from '../wallet/api';

/**
 * "这一期都有谁" —— 该期完整参与名单，旁观者视角。
 *
 * <p>不是 `RaffleResultDetailSheet`（`WalletPage.tsx`）的替代品——那个是**当事人
 * 视角**（"你的号码没中"），从钱包/票券流进入，渲染的是 `my_entries` 的格子而非
 * 全部参与者，且嵌在 4000 行的 `WalletPage.tsx` 里。这里回答的是另一个问题——
 * 这一期都有谁——与 Milhar 的分工完全同构：Milhar History 行身开的是专用的
 * `RoundDetailSheet`（列出该期全部注单），不是钱包里那个个人结果页。
 *
 * <p><b>名单必须完整列出，不许截断。</b> 它就是验算 `(D mod N) + 1` 里的那个
 * N——截断了就数不出来，验算也就无从谈起。后端 `entriesForEvent(event.id(), 1000)`
 * 已经把上限定在 1000；这里只管全量渲染，长列表靠页面滚动，不做"及更多"式的
 * 省略。
 *
 * <p><b>名单不是随 History 行一起来的。</b> `/api/v1/raffle/history` 现在只给
 * 每一期的标量字段（`total_entries`、`winners`、`my_entries`……），不再带
 * `entries`——那个数组挂着每个参与者的 `user_id` + `username` + 头像，50~100
 * 期各自最多 1000 条，全放在一次列表请求里既是几 MB 的 JSON，也是一次性把
 * 上万条 `{user_id, username}` 交给任何登录用户（2026-09-13 复核 Important
 * ×2）。真正的名单只在这里、真正点开一期的时候才取——`GET
 * /api/v1/raffle/events/{eventId}` 本来就是给"这一期的完整结果"用的端点，
 * 原样复用，不新开一条路。
 */
export function DrawEntriesSheet({
  event,
  onClose,
}: {
  event: RaffleRecentEvent;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const token = useAuthStore((state) => state.token);
  // 点某一行 → 在这张名单上方弹出该玩家的公开主页（关注、最近下注、抽奖战绩），
  // 关掉还在这一期名单里。与 Milhar 买家名单（RoundBuyersSheet）同一套做法，
  // 不走「跳去排行榜再返回」那条路——那会离开抽奖历史。
  const [profileFor, setProfileFor] = useState<{ userId: number | string; username: string; avatarUrl?: string } | null>(null);
  const guestMode = useAuthStore((state) => state.guestMode);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const logout = useAuthStore((state) => state.logout);
  const setRoute = useAppStore((state) => state.setRoute);
  const viewer = raffleViewerMode(token, guestMode);
  const isGuest = viewer === 'guest';
  const [guestAuthOpen, setGuestAuthOpen] = useState(false);
  const leaveGuest = (nextRoute: 'auth.register' | 'auth.login') => {
    setGuestAuthOpen(false);
    clearGuest();
    logout();
    setRoute(nextRoute);
  };

  const eventId = String(event.event_id || event.id || '');
  const detailQuery = useQuery({
    queryKey: ['sorteios-draw-entries', token || 'guest', eventId],
    queryFn: () => fetchRaffleResult(token || '', eventId),
    enabled: !!eventId && raffleReadsEnabled(viewer),
    staleTime: 5 * 60_000,
  });

  const winners = event.winners || [];
  const myEntries = event.my_entries || [];
  const totalEntries = Number(event.total_entries || 0);
  const prizesTotal = settledPoolOf(winners, event.prize_brackets, totalEntries);

  // 票号的读法走共享的 ticketSerial/ticketLabel。这里原先是照抄 WalletPage 那份
  // 私有实现的一段（连注释都写明「不是复用同一份代码」）—— 票号从 1 起改成 0 起
  // 时两份抄件都没跟上，名单里每个号都印高了一位（2026-09-25 复核）。
  const winnerBySerial = new Map(winners.map((winner) => [ticketSerial(winner.serial_no), winner]));
  const mySerials = new Set(myEntries.map((entry) => ticketSerial(entry.serial_no)));

  // 全量参与名单，按序号升序——这份列表本身就是验算里的 N，不分页不省略。
  // 来自单独取到的 detailQuery，不是 History 行自带的 event.entries（那个
  // 字段现在的 history 端点不再发）。
  const entries = (detailQuery.data?.entries || [])
    .slice()
    .sort((a, b) => ticketSerial(a.serial_no) - ticketSerial(b.serial_no));

  const anchorRound = String(event.anchor_round_number ?? '').trim();
  // 一期由「本地日期时刻 + global 号」认。**不带日内期号**：它和票号都是
  // 「#三位数」，同屏出现玩家分不清哪个是期、哪个是中奖的票，而 global 号本来
  // 就是客服工单和链上核对要的那个（2026-09-18 PM）。
  const identityLine = [
    event.scheduled_at ? shortDateTime(event.scheduled_at, language) : '',
    anchorRound ? `global #${anchorRound}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div className="ds-screen srt-ent-screen">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button" onClick={onClose} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">
          {t('sorteiosHistoryWho')}
        </h1>
      </header>

      <div className="flex-1 min-h-0 srt-ent-body no-scrollbar">
        {identityLine ? <p className="srt-ent-ident">{identityLine}</p> : null}

        <div className="srt-ent-sum">
          <div>
            <span>{t('sorteiosEntriesParticipants')}</span>
            <b>{fmtInt(totalEntries)}</b>
          </div>
          <div>
            <span>{t('sorteiosEntriesWinners')}</span>
            <b>{fmtInt(winners.length)}</b>
          </div>
          <div>
            <span>{t('sorteiosEntriesPrizes')}</span>
            <b className="is-gold">{fmtMoney(prizesTotal)}</b>
          </div>
        </div>

        {detailQuery.isError ? (
          <div className="milhar-stats-fallback">
            <p>{t('sorteiosLoadError')}</p>
            <button type="button" className="milhar-verify-retry tap"
              onClick={() => detailQuery.refetch()}>{t('milharStatsRetry')}</button>
          </div>
        ) : detailQuery.isPending ? (
          <div className="milhar-stats-skeleton">
            {Array.from({ length: 6 }, (_, i) => <span key={i} className="sk" />)}
          </div>
        ) : (
          <>
            <ul className="srt-ent-list">
              {entries.map((entry) => {
                const serial = ticketSerial(entry.serial_no);
                const winner = winnerBySerial.get(serial);
                const isWin = Boolean(winner);
                const isMe = mySerials.has(serial);
                // 头像：与 RoundDetailSheet（Milhar 的同角色组件）同一套读法——
                // 缩略图优先，没有就用默认头像包 id 拼图，两者都没有就落到首字母块。
                // RaffleEntry 本身就带 avatar_thumb / avatar_default_id（见
                // features/wallet/api.ts），不必去 winners 里找同 serial 的那条借。
                const avatarUrl = entry.avatar_thumb
                  || (entry.avatar_default_id ? defaultAvatarUrl(entry.avatar_default_id) : '');
                const rawName = String(entry.username || '');
                // 买票时刻：序号就是按这个先后发的，摆在名字下面，「为什么他是
                // #001」一眼看明白。按看的人的时区显示；与开奖同一天只给时分秒，
                // 跨天才带日期。**要带秒**：机器人一波买票几十张都落在同一分钟，
                // 只到分钟整列都是「14:00」，序号先后就看不出来了。
                const enteredAt = entry.registered_at
                  ? localListTimeSec(Number(entry.registered_at), Number(event.scheduled_at || 0) || Math.floor(Date.now() / 1000))
                  : '';
                const canOpenProfile = entry.user_id !== undefined && entry.user_id !== null && rawName.trim().length > 0;
                const inner = (
                  <>
                    {avatarUrl
                      ? <img className="srt-ent-av" src={avatarUrl} alt="" width={24} height={24} loading="lazy" />
                      : <span className="srt-ent-av is-letter" style={{ width: 24, height: 24 }}>
                          {rawName.trim().charAt(0).toUpperCase()}</span>}
                    <span className="srt-ent-sn">{ticketLabel(serial)}</span>
                    <span className="srt-ent-nm">
                      <span className="srt-ent-nm-text">{isMe ? t('sorteiosEntriesYou') : rawName}</span>
                      {enteredAt ? <small className="srt-ent-time">{enteredAt}</small> : null}
                    </span>
                    <span className="srt-ent-amt">
                      {isWin ? fmtMoney(Number(winner?.amount || 0)) : '—'}
                    </span>
                  </>
                );
                const rowClass = `srt-ent${isWin ? ' is-win' : ''}${isMe ? ' is-me' : ''}`;
                return (
                  <li key={String(entry.entry_id || entry.serial_no)}>
                    {canOpenProfile ? (
                      <button
                        type="button"
                        className={`${rowClass} srt-ent-btn tap`}
                        aria-label={isGuest ? rawName : t('raffleOpenProfile').replace('%username%', rawName)}
                        onClick={() => (isGuest
                          ? setGuestAuthOpen(true)
                          : setProfileFor({ userId: entry.user_id as number | string, username: rawName, avatarUrl }))}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div className={rowClass}>{inner}</div>
                    )}
                  </li>
                );
              })}
            </ul>

            <p className="srt-ent-note">{t('sorteiosEntriesCountable')}</p>
            {isGuest ? <GuestJoinFooter onSignup={() => leaveGuest('auth.register')} /> : null}
          </>
        )}
      </div>
      {profileFor ? (
        <PublicProfileSheet
          user={profileSeed(profileFor)}
          backdropClassName="sheet-backdrop over-screen"
          onClose={() => setProfileFor(null)}
        />
      ) : null}
      {guestAuthOpen ? (
        <GuestAuthSheet
          backdropClassName="sheet-backdrop over-screen"
          onSignup={() => leaveGuest('auth.register')}
          onLogin={() => leaveGuest('auth.login')}
          onClose={() => setGuestAuthOpen(false)}
        />
      ) : null}
    </div>
  );
}
